import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, TableSortLabel,
  TextField, InputAdornment, Button, CircularProgress, Alert, Snackbar,
  Tooltip, Breadcrumbs, Link, Avatar, Divider, Grid,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import AppsOutlinedIcon from '@mui/icons-material/AppsOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Link as RouterLink } from 'react-router-dom';
import { getPMDashboardData } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────
function fmtDate(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      day: '2-digit', month: 'short', year: 'numeric',
    });
  } catch { return iso; }
}

/** Parse "DD-MMM-YYYY" (e.g. "01-May-2026") into a Date. */
function parseDMY(str) {
  if (!str) return null;
  try {
    const [d, m, y] = str.split('-');
    const dt = new Date(`${m} ${d} ${y}`);
    return isNaN(dt.getTime()) ? null : dt;
  } catch { return null; }
}

function getInitials(name) {
  if (!name) return '?';
  return name.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);
}

// ─────────────────────────────────────────────────────────────────────────────
// KPI Card
// ─────────────────────────────────────────────────────────────────────────────
function KpiCard({ icon, label, value, bgColor, iconColor, subLabel }) {
  return (
    <Card sx={{ flex: '1 1 160px' }}>
      <CardContent sx={{ p: '18px !important' }}>
        <Box sx={{ width: 38, height: 38, borderRadius: 2, bgcolor: bgColor, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
          <Box sx={{ color: iconColor, display: 'flex', fontSize: 20 }}>{icon}</Box>
        </Box>
        <Typography sx={{ fontWeight: 700, color: '#0A1929', fontSize: '1.55rem', lineHeight: 1.15, mb: 0.25 }}>
          {typeof value === 'number' ? value.toLocaleString() : value}
        </Typography>
        <Typography variant="body2" sx={{ color: '#5A6A7A', fontWeight: 500, fontSize: '0.78rem' }}>
          {label}
        </Typography>
        {subLabel && (
          <Typography variant="caption" sx={{ color: iconColor, fontWeight: 600, opacity: 0.85, fontSize: '0.7rem' }}>
            {subLabel}
          </Typography>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Stacked bar (retain | revoke | pending)
// ─────────────────────────────────────────────────────────────────────────────
function StackedBar({ retained, revoked, pending, total, height = 6 }) {
  if (!total) return <Typography variant="caption" color="text.secondary">—</Typography>;
  const donePct = Math.round(((retained + revoked) / total) * 100);
  return (
    <Box>
      <Box sx={{ display: 'flex', height, borderRadius: height / 2, overflow: 'hidden', bgcolor: '#F0F3F7' }}>
        <Box sx={{ width: `${(retained / total) * 100}%`, bgcolor: '#1B8A2D', transition: 'width .5s' }} />
        <Box sx={{ width: `${(revoked / total) * 100}%`, bgcolor: '#C62828', transition: 'width .5s' }} />
        <Box sx={{ width: `${(pending / total) * 100}%`, bgcolor: '#E65100', opacity: .4, transition: 'width .5s' }} />
      </Box>
      <Typography variant="caption" sx={{ color: '#5A6A7A', fontSize: '0.68rem' }}>{donePct}% reviewed</Typography>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Table sort helpers
// ─────────────────────────────────────────────────────────────────────────────

/** Returns the value used for sorting — handles computed 'progress' column. */
function getSortValue(app, key) {
  if (key === 'progress') {
    return app.totalUsers > 0
      ? (app.retained + app.revoked) / app.totalUsers
      : 0;
  }
  return app[key] ?? '';
}

function descCmp(a, b, k) {
  const av = getSortValue(a, k);
  const bv = getSortValue(b, k);
  return bv < av ? -1 : bv > av ? 1 : 0;
}
const getCmp = (order, key) =>
  order === 'desc' ? (a, b) => descCmp(a, b, key) : (a, b) => -descCmp(a, b, key);

const COLUMNS = [
  { id: 'appName', label: 'Application', minWidth: 180 },
  { id: 'appOwner', label: 'App Owner', minWidth: 130 },
  { id: 'appLeader', label: 'App Leader', minWidth: 130 },
  { id: 'dueDate', label: 'Due Date', minWidth: 110, align: 'center' },
  { id: 'totalUsers', label: 'Total Users', minWidth: 90, align: 'center' },
  { id: 'pending', label: 'Pending', minWidth: 80, align: 'center' },
  { id: 'retained', label: 'Retain', minWidth: 80, align: 'center' },
  { id: 'revoked', label: 'Revoke', minWidth: 80, align: 'center' },
  { id: 'progress', label: 'Progress', minWidth: 140 },
  { id: 'status', label: 'Status', minWidth: 110, align: 'center', sortable: false },
];

// Shared sx for per-column filter inputs
const colFilterSx = {
  mt: 0.5, width: '100%',
  '& .MuiInputBase-root': { fontSize: '0.62rem', height: 22, bgcolor: '#fff', borderRadius: 0.5 },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#DDE3EA' },
  '& .MuiInputBase-input': { py: '2px', px: '4px' },
  '& .MuiInputBase-input::placeholder': { fontSize: '0.62rem', opacity: 0.6 },
};

// ─────────────────────────────────────────────────────────────────────────────
// ── WIDGET: Sign-off Progress ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
function SignoffProgressWidget({ apps, loading }) {
  const total = apps.length;
  const signedOff = apps.filter((a) => a.isSignedOff).length;
  const readyToSign = apps.filter((a) => !a.isSignedOff && a.pending === 0 && a.totalUsers > 0).length;
  const inReview = total - signedOff - readyToSign;
  const pct = total > 0 ? Math.round((signedOff / total) * 100) : 0;

  // Sorted: signed-off first (by date desc), then ready-to-sign, then in-review
  const sorted = useMemo(() => {
    return [...apps].sort((a, b) => {
      if (a.isSignedOff && !b.isSignedOff) return -1;
      if (!a.isSignedOff && b.isSignedOff) return 1;
      if (a.isSignedOff && b.isSignedOff) {
        return (b.signoffAt || '') > (a.signoffAt || '') ? 1 : -1;
      }
      // both in-review: ready-to-sign first
      if (a.pending === 0 && b.pending > 0) return -1;
      if (a.pending > 0 && b.pending === 0) return 1;
      return (a.appName || '').localeCompare(b.appName || '');
    });
  }, [apps]);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: '20px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <LockOutlinedIcon sx={{ fontSize: 17, color: '#1976D2' }} />
          <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#0A1929' }}>
            Sign-off Progress
          </Typography>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.78rem' }}>
          {signedOff} signed off · {readyToSign} ready · {inReview} in review
        </Typography>

        {/* Segmented progress bar */}
        <Box sx={{ display: 'flex', height: 8, borderRadius: 4, overflow: 'hidden', bgcolor: '#F0F3F7', mb: 1 }}>
          <Tooltip title={`Signed Off: ${signedOff}`}>
            <Box sx={{ width: `${(signedOff / Math.max(total, 1)) * 100}%`, bgcolor: '#1B8A2D', transition: 'width .6s' }} />
          </Tooltip>
          <Tooltip title={`Ready to Sign Off: ${readyToSign}`}>
            <Box sx={{ width: `${(readyToSign / Math.max(total, 1)) * 100}%`, bgcolor: '#1976D2', transition: 'width .6s' }} />
          </Tooltip>
          <Tooltip title={`In Review: ${inReview}`}>
            <Box sx={{ width: `${(inReview / Math.max(total, 1)) * 100}%`, bgcolor: '#E65100', opacity: .4, transition: 'width .6s' }} />
          </Tooltip>
        </Box>

        {/* Legend row */}
        <Box sx={{ display: 'flex', gap: 2, mb: 2.5, flexWrap: 'wrap' }}>
          {[
            { color: '#1B8A2D', label: 'Signed Off', val: signedOff },
            { color: '#1976D2', label: 'Ready', val: readyToSign },
            { color: '#E65100', label: 'In Review', val: inReview, opacity: .55 },
          ].map(({ color, label, val, opacity }) => (
            <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
              <Box sx={{ width: 8, height: 8, borderRadius: 1, bgcolor: color, opacity: opacity ?? 1 }} />
              <Typography variant="caption" sx={{ color: '#5A6A7A', fontSize: '0.72rem' }}>
                {label}: <strong>{val}</strong>
              </Typography>
            </Box>
          ))}
          <Box sx={{ ml: 'auto' }}>
            <Typography variant="caption" sx={{ fontWeight: 700, color: pct >= 80 ? '#1B8A2D' : pct >= 40 ? '#E65100' : '#C62828', fontSize: '0.78rem' }}>
              {pct}% complete
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* App list */}
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress size={24} sx={{ color: '#1976D2' }} />
          </Box>
        ) : (
          <Box sx={{
            flex: 1, overflowY: 'auto', maxHeight: 340, pr: 0.5,
            '&::-webkit-scrollbar': { width: 4 },
            '&::-webkit-scrollbar-thumb': { bgcolor: '#C4CDD5', borderRadius: 2 },
          }}>
            {sorted.map((app) => {
              const isReady = !app.isSignedOff && app.pending === 0 && app.totalUsers > 0;
              return (
                <Box
                  key={app.appId}
                  sx={{
                    display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.25,
                    borderBottom: '1px solid #F0F3F7',
                    '&:last-child': { borderBottom: 'none' },
                  }}
                >
                  {/* Status icon */}
                  <Box sx={{ mt: 0.1, flexShrink: 0 }}>
                    {app.isSignedOff ? (
                      <CheckCircleIcon sx={{ fontSize: 18, color: '#1B8A2D' }} />
                    ) : isReady ? (
                      <LockOpenOutlinedIcon sx={{ fontSize: 18, color: '#1976D2' }} />
                    ) : (
                      <RadioButtonUncheckedIcon sx={{ fontSize: 18, color: '#D0D7DE' }} />
                    )}
                  </Box>

                  {/* Content */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600, color: '#0A1929', fontSize: '0.8rem',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}
                      >
                        {app.appName}
                      </Typography>
                    </Box>

                    {app.isSignedOff ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <PersonOutlinedIcon sx={{ fontSize: 12, color: '#5A6A7A' }} />
                        <Typography variant="caption" sx={{ color: '#5A6A7A', fontSize: '0.7rem' }}>
                          {app.signoffBy || '—'}&nbsp;·&nbsp;{fmtDate(app.signoffAt)}
                        </Typography>
                      </Box>
                    ) : isReady ? (
                      <Typography variant="caption" sx={{ color: '#1976D2', fontWeight: 600, fontSize: '0.7rem' }}>
                        All reviewed — awaiting sign-off
                      </Typography>
                    ) : (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon sx={{ fontSize: 12, color: '#E65100' }} />
                        <Typography variant="caption" sx={{ color: '#E65100', fontWeight: 600, fontSize: '0.7rem' }}>
                          {app.pending} pending {app.pending === 1 ? 'review' : 'reviews'}
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Right badge */}
                  <Box sx={{ flexShrink: 0 }}>
                    {app.isSignedOff ? (
                      <Chip label="Signed Off" size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#E8F5E9', color: '#1B8A2D', borderRadius: 1 }} />
                    ) : isReady ? (
                      <Chip label="Ready" size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#E3F2FD', color: '#0D47A1', borderRadius: 1 }} />
                    ) : (
                      <Chip label="In Review" size="small"
                        sx={{ height: 20, fontSize: '0.65rem', fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', borderRadius: 1 }} />
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── WIDGET: Reviewers per App ─────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
function ReviewersWidget({ apps, ownersMap, ownersLoading }) {
  // total owner count across all apps
  const totalReviewers = useMemo(() => {
    return Object.values(ownersMap).reduce((sum, owners) => sum + owners.length, 0);
  }, [ownersMap]);

  const appsWithReviewers = useMemo(() =>
    apps
      .map((app) => {
        const reviewers = ownersMap[app.appId] ?? [];
        return { ...app, reviewers };
      })
      .sort((a, b) => b.reviewers.length - a.reviewers.length),
    [apps, ownersMap]);

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardContent sx={{ p: '20px !important', flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GroupOutlinedIcon sx={{ fontSize: 17, color: '#1976D2' }} />
            <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#0A1929' }}>
              Reviewers per Application
            </Typography>
          </Box>
          {ownersLoading && <CircularProgress size={14} sx={{ color: '#1976D2' }} />}
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.78rem' }}>
          {totalReviewers} reviewer{totalReviewers !== 1 ? 's' : ''} assigned across {apps.length} app{apps.length !== 1 ? 's' : ''}
        </Typography>

        <Divider sx={{ mb: 2 }} />

        {/* List */}
        <Box sx={{
          flex: 1, overflowY: 'auto', maxHeight: 380, pr: 0.5,
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { bgcolor: '#C4CDD5', borderRadius: 2 },
        }}>
          {appsWithReviewers.map((app) => (
            <Box
              key={app.appId}
              sx={{
                py: 1.5, borderBottom: '1px solid #F0F3F7',
                '&:last-child': { borderBottom: 'none' },
              }}
            >
              {/* App name + reviewer count badge */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.75 }}>
                <Typography variant="body2" sx={{
                  fontWeight: 600, color: '#0A1929', fontSize: '0.8rem',
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '65%'
                }}>
                  {app.appName}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                  <Chip
                    icon={<PersonOutlinedIcon sx={{ fontSize: '11px !important' }} />}
                    label={`${app.reviewers.length} reviewer${app.reviewers.length !== 1 ? 's' : ''}`}
                    size="small"
                    sx={{
                      height: 20, fontSize: '0.66rem', fontWeight: 600, borderRadius: 1,
                      bgcolor: app.reviewers.length > 0 ? '#EBF5FF' : '#F4F6F9',
                      color: app.reviewers.length > 0 ? '#0D47A1' : '#9AA5B4',
                    }}
                  />
                </Box>
              </Box>

              {app.reviewers.length === 0 ? (
                <Typography variant="caption" sx={{ color: '#B0BECA', fontStyle: 'italic', fontSize: '0.7rem' }}>
                  No reviewers assigned
                </Typography>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
                  {app.reviewers.map((r) => (
                    <Box key={r.userid} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar
                        sx={{
                          width: 22, height: 22, fontSize: '0.6rem', fontWeight: 700,
                          bgcolor: '#1A3A5C', color: '#fff', flexShrink: 0
                        }}
                      >
                        {getInitials(r.fullName || r.userid)}
                      </Avatar>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" sx={{
                          fontWeight: 600, color: '#1A2027', fontSize: '0.72rem', display: 'block',
                          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                        }}>
                          {r.fullName || r.userid}
                        </Typography>
                        {(r.title || r.department) && (
                          <Typography variant="caption" sx={{
                            color: '#5A6A7A', fontSize: '0.67rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block'
                          }}>
                            {[r.title, r.department].filter(Boolean).join(' · ')}
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          ))}
        </Box>
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// ── Main Page ─────────────────────────────────────────────────────────────────
// ─────────────────────────────────────────────────────────────────────────────
export default function PMDashboardPage() {
  const { user } = useAuth();

  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ownersMap, setOwnersMap] = useState({});
  const [error, setError] = useState('');
  const [toastOpen, setToastOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [colFilters, setColFilters] = useState({});
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('progress');

  const updateColFilter = useCallback((colId, value) => {
    setColFilters((prev) => ({ ...prev, [colId]: value }));
  }, []);

  // ── Load all apps + reviewer data in one call ─────────────────────────────
  const loadData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { apps: fetchedApps, ownersMap: fetchedOwnersMap } = await getPMDashboardData();
      setApps(fetchedApps);
      setOwnersMap(fetchedOwnersMap);
    } catch (err) {
      setError(`Failed to load applications: ${err.message}`);
      setToastOpen(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  // ── Aggregate KPIs ────────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const totalApps = apps.length;
    const totalUsers = apps.reduce((s, a) => s + (a.totalUsers ?? 0), 0);
    const totalPending = apps.reduce((s, a) => s + (a.pending ?? 0), 0);
    const totalRetained = apps.reduce((s, a) => s + (a.retained ?? 0), 0);
    const totalRevoked = apps.reduce((s, a) => s + (a.revoked ?? 0), 0);
    const signedOff = apps.filter((a) => a.isSignedOff).length;
    const overallPct = totalUsers > 0
      ? Math.round(((totalRetained + totalRevoked) / totalUsers) * 100) : 0;
    const signedOffPct = totalApps > 0
      ? Math.round((signedOff / totalApps) * 100) : 0;
    return { totalApps, totalUsers, totalPending, totalRetained, totalRevoked, signedOff, overallPct, signedOffPct };
  }, [apps]);

  // ── Sort / filter for table ───────────────────────────────────────────────
  const handleSort = (col) => {
    setOrder(orderBy === col && order === 'asc' ? 'desc' : 'asc');
    setOrderBy(col);
  };

  const filteredApps = useMemo(() => {
    let list = apps;

    // Global search
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((a) =>
        a.appName?.toLowerCase().includes(q) ||
        a.appOwner?.toLowerCase().includes(q) ||
        a.appLeader?.toLowerCase().includes(q)
      );
    }

    // Per-column filters
    const NUMERIC_COLS = new Set(['totalUsers', 'pending', 'retained', 'revoked', 'progress']);

    /** Parse a filter string and test it against a numeric value.
     *  Supports: ">50"  ">=50"  "<50"  "<=50"  "10-50"  "50" (exact) */
    function matchesNumeric(raw, num) {
      const v = raw.trim().replace('%', '');
      const rangeMatch = v.match(/^(\d+)\s*-\s*(\d+)$/);
      if (rangeMatch) return num >= Number(rangeMatch[1]) && num <= Number(rangeMatch[2]);
      const opMatch = v.match(/^(>=|<=|>|<)\s*(\d+)$/);
      if (opMatch) {
        const n = Number(opMatch[2]);
        switch (opMatch[1]) {
          case '>': return num > n;
          case '>=': return num >= n;
          case '<': return num < n;
          case '<=': return num <= n;
          default: return false;
        }
      }
      if (/^\d+$/.test(v)) return num === Number(v);
      return String(num).includes(v);
    }

    const activeFilters = Object.entries(colFilters).filter(([, v]) => v && v.trim());
    if (activeFilters.length > 0) {
      list = list.filter((a) =>
        activeFilters.every(([colId, val]) => {
          if (NUMERIC_COLS.has(colId)) {
            const num = colId === 'progress'
              ? (a.totalUsers > 0 ? Math.round(((a.retained + a.revoked) / a.totalUsers) * 100) : 0)
              : (a[colId] ?? 0);
            return matchesNumeric(val, num);
          }
          if (colId === 'status') {
            const label = a.isSignedOff
              ? 'signed off'
              : a.pending === 0 && a.totalUsers > 0
                ? 'ready to sign off'
                : 'in review';
            return label.includes(val.toLowerCase());
          }
          return String(a[colId] ?? '').toLowerCase().includes(val.toLowerCase());
        })
      );
    }

    return [...list].sort(getCmp(order, orderBy));
  }, [apps, search, colFilters, order, orderBy]);

  // ── Per-app bars sorted by completion ────────────────────────────────────
  const appsByCompletion = useMemo(() =>
    [...apps].sort((a, b) => {
      const ap = a.totalUsers > 0 ? (a.retained + a.revoked) / a.totalUsers : 0;
      const bp = b.totalUsers > 0 ? (b.retained + b.revoked) / b.totalUsers : 0;
      return bp - ap;
    }), [apps]);

  // ── CSV Export ────────────────────────────────────────────────────────────
  const handleExport = () => {
    const headers = ['App Name', 'App Owner', 'App Leader', 'Due Date', 'Total Users', 'Pending', 'Retained', 'Revoked', 'Progress %', 'Status'];
    const rows = filteredApps.map((a) => {
      const pct = a.totalUsers > 0 ? Math.round(((a.retained + a.revoked) / a.totalUsers) * 100) : 0;
      return [
        a.appName, a.appOwner, a.appLeader,
        a.dueDate ?? '',
        a.totalUsers, a.pending, a.retained, a.revoked, `${pct}%`,
        a.isSignedOff ? 'Signed Off' : 'In Review',
      ].map((v) => `"${String(v ?? '').replace(/"/g, '""')}"`).join(',');
    });
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `admin-dashboard-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ─────────────────────────────────────────────────────────────────────────
  return (
    <Box>
      {/* ── Breadcrumbs ─────────────────────────────────────────────────── */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/home" underline="hover" color="inherit"
          sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: '0.825rem' }}>
          <HomeOutlinedIcon sx={{ fontSize: 16 }} /> Home
        </Link>
        <Typography variant="body2" color="text.primary" fontWeight={600}>Admin Dashboard</Typography>
      </Breadcrumbs>

      {/* ── Page header ──────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ color: '#0A1929', mb: 0.5 }}>Review Cycle Overview</Typography>
          <Typography variant="body2" color="text.secondary">
            Access review progress across all applications —{' '}
            <strong>{user?.name || user?.userId}</strong>
          </Typography>
        </Box>
        <Button variant="outlined"
          startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />}
          onClick={loadData} disabled={loading} size="small"
          sx={{ borderColor: '#D0D7DE', color: '#1A2027', height: 36 }}>
          Refresh
        </Button>
      </Box>

      {/* ── KPI Cards ──────────────────────────────────────────────────────── */}
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <KpiCard icon={<AppsOutlinedIcon fontSize="inherit" />}
          label="Total Applications" value={stats.totalApps}
          bgColor="#EBF5FF" iconColor="#0D47A1" />
        <KpiCard icon={<PeopleAltOutlinedIcon fontSize="inherit" />}
          label="Total Aggregated User Accesses" value={stats.totalUsers}
          bgColor="#F3E5F5" iconColor="#6A1B9A" />
        <KpiCard icon={<TaskAltIcon fontSize="inherit" />}
          label="Signed Off" value={stats.signedOff}
          bgColor="#E8F5E9" iconColor="#1B8A2D"
          subLabel={`${stats.signedOffPct}% of apps complete`} />
        <KpiCard icon={<PendingActionsOutlinedIcon fontSize="inherit" />}
          label="Pending Access Reviews" value={stats.totalPending}
          bgColor="#FFF3E0" iconColor="#E65100" />
        <KpiCard icon={<CheckCircleOutlineIcon fontSize="inherit" />}
          label="Retained Accesses" value={stats.totalRetained}
          bgColor="#E8F5E9" iconColor="#1B8A2D" />
        <KpiCard icon={<HighlightOffIcon fontSize="inherit" />}
          label="Revoked Accesses" value={stats.totalRevoked}
          bgColor="#FFEBEE" iconColor="#C62828" />
      </Box>

      {/* ── Overall Review Progress ───────────────────────────────────────── */}
      <Card sx={{ mb: 3 }}>
        <CardContent sx={{ p: '20px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2, flexWrap: 'wrap', gap: 1 }}>
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.4 }}>
                <AssessmentOutlinedIcon sx={{ fontSize: 18, color: '#1976D2' }} />
                <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#0A1929' }}>
                  Overall Review Progress
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {stats.signedOff} of {stats.totalApps} applications signed off —{' '}
                {stats.totalRetained + stats.totalRevoked} of {stats.totalUsers} users reviewed
              </Typography>
            </Box>
            <Box sx={{
              bgcolor: stats.overallPct >= 80 ? '#E8F5E9' : stats.overallPct >= 40 ? '#FFF3E0' : '#FFEBEE',
              color: stats.overallPct >= 80 ? '#1B8A2D' : stats.overallPct >= 40 ? '#E65100' : '#C62828',
              fontWeight: 700, fontSize: '1.15rem', borderRadius: 2, px: 2, py: 0.75,
            }}>
              {stats.overallPct}%
            </Box>
          </Box>

          {stats.totalUsers > 0 && (
            <Tooltip title={`Retained ${stats.totalRetained} · Revoked ${stats.totalRevoked} · Pending ${stats.totalPending}`} placement="top">
              <Box sx={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', bgcolor: '#F0F3F7', mb: 1.5, cursor: 'default' }}>
                <Box sx={{ width: `${(stats.totalRetained / stats.totalUsers) * 100}%`, bgcolor: '#1B8A2D', transition: 'width .6s ease' }} />
                <Box sx={{ width: `${(stats.totalRevoked / stats.totalUsers) * 100}%`, bgcolor: '#C62828', transition: 'width .6s ease' }} />
                <Box sx={{ width: `${(stats.totalPending / stats.totalUsers) * 100}%`, bgcolor: '#E65100', opacity: .45, transition: 'width .6s ease' }} />
              </Box>
            </Tooltip>
          )}

          <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
            {[
              { color: '#1B8A2D', label: 'Retained', value: stats.totalRetained },
              { color: '#C62828', label: 'Revoked', value: stats.totalRevoked },
              { color: '#E65100', label: 'Pending', value: stats.totalPending, opacity: .55 },
            ].map(({ color, label, value, opacity }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                <Box sx={{ width: 10, height: 10, borderRadius: 1, bgcolor: color, opacity: opacity ?? 1 }} />
                <Typography variant="body2" sx={{ color: '#5A6A7A', fontSize: '0.78rem' }}>
                  {label}: <strong>{value.toLocaleString()}</strong>
                </Typography>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>

      {/* ── App-wise Table ──────────────────────────────────────────────────── */}
      <Card sx={{ mt: 3 }}>
        <CardContent sx={{ p: '20px !important' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
            <Box>
              <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#0A1929', mb: 0.2 }}>
                Application-wise Review Summary
              </Typography>
              {!loading && (
                <Typography variant="caption" color="text.secondary">
                  {filteredApps.length} of {apps.length} applications
                </Typography>
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap' }}>
              <TextField size="small" placeholder="Search name, owner, leader…"
                value={search} onChange={(e) => setSearch(e.target.value)}
                sx={{ minWidth: 250 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ fontSize: 18, color: '#9AA5B4' }} />
                    </InputAdornment>
                  )
                }} />
              {Object.values(colFilters).some((v) => v && v.trim()) && (
                <Chip
                  label="Clear filters"
                  size="small"
                  onDelete={() => setColFilters({})}
                  onClick={() => setColFilters({})}
                  sx={{ bgcolor: '#EBF5FF', color: '#0D47A1', fontWeight: 600, fontSize: '0.65rem', height: 24 }}
                />
              )}
              <Button variant="outlined" startIcon={<DownloadIcon />}
                onClick={handleExport} size="small"
                disabled={loading || apps.length === 0}
                sx={{ borderColor: '#D0D7DE', color: '#1A2027', whiteSpace: 'nowrap' }}>
                Export CSV
              </Button>
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 10, gap: 2 }}>
              <CircularProgress size={32} sx={{ color: '#1976D2' }} />
              <Typography variant="body2" color="text.secondary">Loading applications…</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ borderRadius: 1.5, border: '1px solid #E3E8EF', overflow: 'auto', maxHeight: 520 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 36, pr: 0, color: '#9AA5B4', verticalAlign: 'top' }}>#</TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.id} align={col.align ?? 'left'}
                        style={{ minWidth: col.minWidth }}
                        sx={{ verticalAlign: 'top' }}
                        sortDirection={orderBy === col.id ? order : false}>
                        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                          <Box>
                            {col.sortable === false ? (
                              <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: '#5A6A7A' }}>
                                {col.label}
                              </Typography>
                            ) : (
                              <TableSortLabel active={orderBy === col.id}
                                direction={orderBy === col.id ? order : 'asc'}
                                onClick={() => handleSort(col.id)}
                                sx={{ '& .MuiTableSortLabel-icon': { fontSize: 12 } }}>
                                <Typography sx={{ fontSize: '0.65rem', fontWeight: 600, color: orderBy === col.id ? '#1976D2' : '#5A6A7A' }}>
                                  {col.label}
                                </Typography>
                              </TableSortLabel>
                            )}
                          </Box>
                          <Tooltip
                            title={['totalUsers', 'pending', 'retained', 'revoked', 'progress'].includes(col.id)
                              ? 'Supports: 50  >50  >=50  <50  <=50  10-50'
                              : ''}
                            placement="bottom"
                            disableHoverListener={!['totalUsers', 'pending', 'retained', 'revoked', 'progress'].includes(col.id)}
                          >
                            <TextField
                              size="small"
                              variant="outlined"
                              placeholder="Filter…"
                              value={colFilters[col.id] || ''}
                              onChange={(e) => updateColFilter(col.id, e.target.value)}
                              onClick={(e) => e.stopPropagation()}
                              sx={colFilterSx}
                            />
                          </Tooltip>
                        </Box>
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredApps.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={COLUMNS.length + 1} align="center" sx={{ py: 7, color: '#9AA5B4' }}>
                        {search ? 'No applications match your search.' : 'No applications found.'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredApps.map((app, idx) => {
                      const donePct = app.totalUsers > 0
                        ? Math.round(((app.retained + app.revoked) / app.totalUsers) * 100) : 0;
                      return (
                        <TableRow key={app.appId} hover>
                          <TableCell sx={{ color: '#B0BECA', fontSize: '0.68rem', pr: 0 }}>{idx + 1}</TableCell>
                          <TableCell sx={{ fontWeight: 600, color: '#0A1929' }}>{app.appName}</TableCell>
                          <TableCell>{app.appOwner || '—'}</TableCell>
                          <TableCell>{app.appLeader || '—'}</TableCell>
                          <TableCell align="center">
                            {(() => {
                              if (!app.dueDate) return <Typography variant="caption" color="text.secondary">—</Typography>;
                              const dt = parseDMY(app.dueDate);
                              const diff = dt ? Math.ceil((dt - new Date()) / (1000 * 60 * 60 * 24)) : null;
                              const isOverdue = diff !== null && diff < 0;
                              const isClose = diff !== null && !isOverdue && diff <= 7;
                              const chipColor = isOverdue ? { bg: '#FFEBEE', fg: '#C62828' }
                                : isClose ? { bg: '#FFF3E0', fg: '#E65100' }
                                  : { bg: '#E8F5E9', fg: '#1B8A2D' };
                              const tipText = isOverdue ? 'Overdue' : isClose ? `${diff} day(s) left` : '';
                              return (
                                <Tooltip title={tipText} placement="top" disableHoverListener={!tipText}>
                                  <Chip
                                    label={app.dueDate}
                                    size="small"
                                    sx={{
                                      height: 20, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1,
                                      bgcolor: chipColor.bg, color: chipColor.fg
                                    }}
                                  />
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={app.totalUsers.toLocaleString()} size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#EBF5FF', color: '#0D47A1', borderRadius: 1 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={app.pending.toLocaleString()} size="small"
                              sx={{
                                height: 20, fontSize: '0.7rem', fontWeight: 600, borderRadius: 1,
                                bgcolor: app.pending > 0 ? '#FFF3E0' : '#F4F6F9',
                                color: app.pending > 0 ? '#E65100' : '#9AA5B4'
                              }} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={app.retained.toLocaleString()} size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#E8F5E9', color: '#1B8A2D', borderRadius: 1 }} />
                          </TableCell>
                          <TableCell align="center">
                            <Chip label={app.revoked.toLocaleString()} size="small"
                              sx={{ height: 20, fontSize: '0.7rem', fontWeight: 600, bgcolor: '#FFEBEE', color: '#C62828', borderRadius: 1 }} />
                          </TableCell>
                          <TableCell sx={{ minWidth: 140 }}>
                            <StackedBar retained={app.retained} revoked={app.revoked}
                              pending={app.pending} total={app.totalUsers} />
                          </TableCell>
                          <TableCell align="center">
                            {app.isSignedOff ? (
                              <Chip icon={<LockOutlinedIcon sx={{ fontSize: '12px !important' }} />}
                                label="Signed Off" size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#E8F5E9', color: '#1B8A2D', borderRadius: 1.5 }} />
                            ) : app.pending === 0 && app.totalUsers > 0 ? (
                              <Chip label="Ready to Sign Off" size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#E3F2FD', color: '#0D47A1', borderRadius: 1.5 }} />
                            ) : (
                              <Chip label="In Review" size="small"
                                sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, bgcolor: '#FFF3E0', color: '#E65100', borderRadius: 1.5 }} />
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      {/* ── Sign-off Progress + Reviewers (side by side) ─────────────────── */}
      <Grid container spacing={3} sx={{ mt: 3, mb: 3 }}>
        <Grid item xs={12} md={6}>
          <SignoffProgressWidget apps={apps} loading={loading} />
        </Grid>
        <Grid item xs={12} md={6}>
          <ReviewersWidget apps={apps} ownersMap={ownersMap} ownersLoading={loading} />
        </Grid>
      </Grid>

      {/* ── Per-Application Completion (mini bars) ────────────────────────── */}
      {appsByCompletion.length > 0 && (
        <Card sx={{ mt: 3 }}>
          <CardContent sx={{ p: '20px !important' }}>
            <Typography variant="h6" sx={{ fontSize: '0.95rem', color: '#0A1929', mb: 2 }}>
              Per-Application Completion
            </Typography>
            <Box sx={{
              display: 'flex', flexDirection: 'column', gap: 1.5,
              maxHeight: 400, overflowY: 'auto',
              pr: 0.5,
              '&::-webkit-scrollbar': { width: 4 },
              '&::-webkit-scrollbar-thumb': { bgcolor: '#C4CDD5', borderRadius: 2 },
            }}>
              {appsByCompletion.map((app) => {
                const donePct = app.totalUsers > 0
                  ? Math.round(((app.retained + app.revoked) / app.totalUsers) * 100) : 0;
                return (
                  <Box key={app.appId}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5, gap: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                        <Typography variant="body2"
                          sx={{
                            fontWeight: 600, color: '#0A1929', fontSize: '0.8rem',
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                          }}>
                          {app.appName}
                        </Typography>
                        {app.isSignedOff && (
                          <Chip label="Signed Off" size="small"
                            icon={<LockOutlinedIcon sx={{ fontSize: '11px !important' }} />}
                            sx={{ height: 18, fontSize: '0.62rem', fontWeight: 700, bgcolor: '#E8F5E9', color: '#1B8A2D', borderRadius: 1 }} />
                        )}
                      </Box>
                      <Typography variant="caption" sx={{ color: '#5A6A7A', fontWeight: 600, ml: 1, whiteSpace: 'nowrap' }}>
                        {donePct}%&nbsp;({app.retained + app.revoked}/{app.totalUsers})
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', height: 6, borderRadius: 3, overflow: 'hidden', bgcolor: '#F0F3F7' }}>
                      {app.totalUsers > 0 && (
                        <>
                          <Box sx={{ width: `${(app.retained / app.totalUsers) * 100}%`, bgcolor: '#1B8A2D' }} />
                          <Box sx={{ width: `${(app.revoked / app.totalUsers) * 100}%`, bgcolor: '#C62828' }} />
                          <Box sx={{ width: `${(app.pending / app.totalUsers) * 100}%`, bgcolor: '#E65100', opacity: .4 }} />
                        </>
                      )}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          </CardContent>
        </Card>
      )}

      {/* ── Error toast ──────────────────────────────────────────────────────── */}
      <Snackbar open={toastOpen} autoHideDuration={6000} onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="error" variant="filled" onClose={() => setToastOpen(false)} sx={{ fontSize: '0.8rem' }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
}
